import React, { useState, useRef, useCallback } from "react";

interface MediaUploadComponentProps {
    maxFiles?: number;
    onFilesReady: (files: File[]) => void;
    acceptedFileTypes?: string;
    disabled?: boolean;
}

export const MediaUploadComponent: React.FC<MediaUploadComponentProps> = ({
    maxFiles = 1,
    onFilesReady,
    acceptedFileTypes = "image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp,image/tiff,image/svg+xml,video/mp4,video/quicktime,video/mpeg,video/webm,video/x-ms-wmv,video/3gpp,video/x-flv,video/x-mpegurl",
    disabled = false,
}) => {
    const [files, setFiles] = useState<File[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const validateFile = (file: File): string | null => {
        const maxFileSize = 50 * 1024 * 1024; // 50MB
        if (file.size > maxFileSize) {
            return `${file.name} exceeds 50MB limit`;
        }
        return null;
    };

    const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(event.target.files || []);
        const newErrors: string[] = [];
        const validFiles: File[] = [];

        selectedFiles.forEach(file => {
            const error = validateFile(file);
            if (error) {
                newErrors.push(error);
            } else {
                validFiles.push(file);
            }
        });

        if (files.length + validFiles.length > maxFiles) {
            newErrors.push(`Maximum ${maxFiles} files allowed`);
            const allowedCount = maxFiles - files.length;
            validFiles.splice(allowedCount);
        }

        setFiles(prev => [...prev, ...validFiles]);
        setErrors(newErrors);

        // Clear the input so the same file can be selected again
        if (event.target) {
            event.target.value = '';
        }
    }, [files.length, maxFiles]);

    const removeFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
        setErrors([]);
    };

    const handleAddClick = () => {
        fileInputRef.current?.click();
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return bytes + ' bytes';
        else if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
        else return Math.round(bytes / 1048576) + ' MB';
    };

    const getFileIcon = (file: File): string => {
        if (file.type.startsWith('video/')) {
            return '🎥';
        }
        return '🖼️';
    };

    React.useEffect(() => {
        onFilesReady(files);
    }, [files, onFilesReady]);

    return (
        <div style={{ width: '100%' }}>
            {/* File list */}
            {files.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                    {files.map((file, index) => (
                        <div
                            key={index}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '8px',
                                marginBottom: '8px',
                                border: '1px solid #e5e7eb',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                <span style={{ fontSize: '24px', marginRight: '12px' }}>
                                    {getFileIcon(file)}
                                </span>
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        fontWeight: '500',
                                        color: '#111827',
                                        fontSize: '14px',
                                        wordBreak: 'break-word',
                                    }}>
                                        {file.name}
                                    </div>
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#6b7280',
                                        marginTop: '2px',
                                    }}>
                                        {formatFileSize(file.size)}
                                    </div>
                                </div>
                                <button
                                    onClick={() => removeFile(index)}
                                    style={{
                                        marginLeft: '12px',
                                        padding: '4px 8px',
                                        backgroundColor: '#fee2e2',
                                        color: '#dc2626',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        transition: 'all 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#fecaca';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#fee2e2';
                                    }}
                                    disabled={disabled}
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add file button */}
            {files.length < maxFiles && (
                <div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={acceptedFileTypes}
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                        multiple={maxFiles > 1}
                        disabled={disabled}
                    />
                    <button
                        onClick={handleAddClick}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            padding: '16px',
                            backgroundColor: disabled ? '#e5e7eb' : '#eff6ff',
                            border: `2px dashed ${disabled ? '#9ca3af' : '#3b82f6'}`,
                            borderRadius: '8px',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            color: disabled ? '#6b7280' : '#2563eb',
                            fontWeight: '500',
                        }}
                        onMouseEnter={(e) => {
                            if (!disabled) {
                                e.currentTarget.style.backgroundColor = '#dbeafe';
                                e.currentTarget.style.borderColor = '#2563eb';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!disabled) {
                                e.currentTarget.style.backgroundColor = '#eff6ff';
                                e.currentTarget.style.borderColor = '#3b82f6';
                            }
                        }}
                        disabled={disabled}
                    >
                        <span style={{ marginRight: '8px', fontSize: '20px' }}>
                            {files.length === 0 ? '📁' : '➕'}
                        </span>
                        {files.length === 0 
                            ? 'Choose images or videos'
                            : `Add more (${files.length}/${maxFiles})`
                        }
                    </button>
                </div>
            )}

            {/* Error messages */}
            {errors.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                    {errors.map((error, index) => (
                        <div
                            key={index}
                            style={{
                                padding: '8px 12px',
                                backgroundColor: '#fee2e2',
                                color: '#dc2626',
                                borderRadius: '6px',
                                fontSize: '14px',
                                marginBottom: '4px',
                            }}
                        >
                            ⚠️ {error}
                        </div>
                    ))}
                </div>
            )}

            {/* Help text */}
            <div style={{
                marginTop: '12px',
                fontSize: '12px',
                color: '#6b7280',
                textAlign: 'center',
            }}>
                Supported: JPEG, PNG, GIF, WebP, MP4, MOV, etc. • Max 50MB per file
            </div>
        </div>
    );
}; 